"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollTextIcon, ShieldCheckIcon } from "lucide-react";
import { useUserSettings } from "@/components/custom/contexts/userSettingsContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SettingsPageHeader } from "./settings-page-header";
import { useAuth } from "@/lib/auth/AuthContext";
import { getAssistantBaseUrl, getDevAdminMode, setDevAdminMode } from "@/lib/authFetch";

export function SettingsDevToolsContent() {
  const { t } = useTranslation();
  const { settings, setDebugMode } = useUserSettings();
  const { refresh } = useAuth();

  const [adminMode, setAdminMode] = useState(getDevAdminMode);

  const handleDebugChange = useCallback(
    (checked: boolean) => {
      setDebugMode(checked);
    },
    [setDebugMode],
  );

  const handleAdminChange = useCallback(
    (checked: boolean) => {
      setAdminMode(checked);
      setDevAdminMode(checked);
      void refresh();
    },
    [refresh],
  );

  useEffect(() => {
    setAdminMode(getDevAdminMode());
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-4">
        <div className="mx-auto max-w-[880px]">
          <SettingsPageHeader
            breadcrumbs={[
              { labelKey: "settings.breadcrumb.root", href: "/settings" },
              { labelKey: "settings.nav.devTools" },
            ]}
            titleKey="settings.debug.title"
            subtitleKey="settings.debug.subtitle"
          />

          {/* Debug mode */}
          <section className="mt-8">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium text-[13.5px]">
                  {t("settings.debug.label")}
                </span>
                <p className="mt-1 max-w-[480px] text-[12.5px] text-muted-foreground leading-relaxed">
                  {t("settings.debug.description")}
                </p>
              </div>
              <Switch
                checked={settings.isDebugMode}
                onCheckedChange={handleDebugChange}
              />
            </div>

            {/* Admin mode */}
            <div className="mt-5 flex items-center justify-between border-border/30 border-t pt-5">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="size-4 text-muted-foreground" />
                <div>
                  <span className="font-medium text-[13.5px]">
                    {t("settings.debug.adminMode")}
                  </span>
                  <p className="mt-1 max-w-[480px] text-[12.5px] text-muted-foreground leading-relaxed">
                    {t("settings.debug.adminModeDescription")}
                  </p>
                </div>
              </div>
              <Switch
                checked={adminMode}
                onCheckedChange={handleAdminChange}
              />
            </div>

            {/* Server logs */}
            <div className="mt-5 border-border/30 border-t pt-5">
              <div className="flex items-center gap-2">
                <ScrollTextIcon className="size-4 text-muted-foreground" />
                <div>
                  <span className="font-medium text-[13.5px]">
                    {t("settings.debug.logs")}
                  </span>
                  <p className="mt-1 max-w-[480px] text-[12.5px] text-muted-foreground leading-relaxed">
                    {t("settings.debug.logsDescription")}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 ml-6"
                onClick={() => { window.location.href = `${getAssistantBaseUrl()}/api/serilog-ui`; }}
              >
                <ScrollTextIcon className="mr-2 size-3.5" />
                {t("settings.debug.openLogs")}
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
